import { getImageContent } from "@/infrastructure/services/imageService";
import { isNgrokUrl } from "@/utils/image-url";
import { useEffect, useState } from "react";

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function useImageSource(source?: string | null) {
  // Nunca expõe uma URL do ngrok ao <img> nem mesmo na primeira renderização.
  // O useEffect roda depois do render; iniciar com `source` disparava um GET
  // direto antes que a URL pudesse ser substituída pelo blob vindo do proxy.
  const [resolvedSource, setResolvedSource] = useState(() =>
    source && !isNgrokUrl(source) ? source : "",
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!source || !isNgrokUrl(source)) {
      setResolvedSource(source ?? "");
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setResolvedSource("");
    setIsLoading(true);

    // Proxy temporário para ambientes cujo CSP bloqueia conexões diretas ao ngrok.
    getImageContent(source, controller.signal)
      .then(blobToDataUrl)
      .then((dataUrl) => {
        if (!controller.signal.aborted) setResolvedSource(dataUrl);
      })
      .catch((error) => {
        if (
          (error instanceof DOMException && error.name === "AbortError") ||
          controller.signal.aborted
        ) return;
        setResolvedSource("");
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    /*
     * ROLLBACK: carregamento direto utilizado antes do proxy.
     * fetch(source, {
     *   headers: { "ngrok-skip-browser-warning": "true" },
     *   signal: controller.signal,
     * }).then((response) => response.blob());
     */

    return () => {
      controller.abort();
    };
  }, [source]);

  return { imageSource: resolvedSource, isLoading };
}
